import React, { useState, useEffect } from 'react';
import { Card, Tag, Button, Select, Input, Space, Typography, Avatar, message, Row, Col, Statistic, Divider, Popconfirm, Descriptions, Badge, Modal, Form } from 'antd';
import { ArrowLeftOutlined, StopOutlined, CheckOutlined, SoundOutlined, MutedOutlined, UserOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import api from '../api';
import { useNavigate, useParams } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;

const genderMap = { 1: '男', 2: '女', 0: '-' };

function UserDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [tags, setTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tagModal, setTagModal] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [muteModal, setMuteModal] = useState(false);
  const [muteHours, setMuteHours] = useState(24);

  const loadUser = async () => {
    setLoading(true);
    try {
      const data = await api.get(`/admin/users/${id}`);
      setUser(data);
      setTags(data.tags || []);
    } catch (e) {
      message.error('加载用户信息失败');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableTags = async () => {
    try {
      const { list } = await api.get('/admin/user-tags', { params: { limit: 100 } });
      setAvailableTags(list || []);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    loadUser();
    loadAvailableTags();
  }, [id]);

  const handleDisable = async () => {
    try {
      await api.put(`/admin/users/${id}/disable`);
      message.success('用户已禁用');
      loadUser();
    } catch (e) {
      message.error('操作失败');
    }
  };

  const handleEnable = async () => {
    try {
      await api.put(`/admin/users/${id}/enable`);
      message.success('用户已启用');
      loadUser();
    } catch (e) {
      message.error('操作失败');
    }
  };

  const handleMute = async () => {
    try {
      await api.put(`/admin/users/${id}/mute`, { hours: muteHours });
      message.success(`已禁言 ${muteHours} 小时`);
      setMuteModal(false);
      loadUser();
    } catch (e) {
      message.error('操作失败');
    }
  };

  const handleUnmute = async () => {
    try {
      await api.put(`/admin/users/${id}/unmute`);
      message.success('已取消禁言');
      loadUser();
    } catch (e) {
      message.error('操作失败');
    }
  };

  const handleAddTags = async () => {
    try {
      await api.post(`/admin/users/${id}/tags`, { tag_ids: selectedTags });
      message.success('标签添加成功');
      setTagModal(false);
      setSelectedTags([]);
      loadUser();
    } catch (e) {
      message.error('添加标签失败');
    }
  };

  const handleRemoveTag = async (tagId) => {
    try {
      await api.delete(`/admin/users/${id}/tags/${tagId}`);
      message.success('标签已移除');
      loadUser();
    } catch (e) {
      message.error('移除标签失败');
    }
  };

  if (!user) {
    return <Card loading={loading} style={{ minHeight: 400 }} />;
  }

  const isMuted = user.muted === 1 && user.mute_until && new Date(user.mute_until) > new Date();

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'users' }))}>
          返回用户列表
        </Button>
      </div>

      <Row gutter={24}>
        {/* 用户信息卡片 */}
        <Col span={16}>
          <Card title="用户信息" loading={loading}>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 24 }}>
              <Avatar
                size={80}
                src={user.avatar}
                icon={<UserOutlined />}
                style={{ marginRight: 24, flexShrink: 0 }}
              />
              <div style={{ flex: 1 }}>
                <Title level={4} style={{ marginBottom: 4 }}>
                  {user.nickname}
                  {user.status === 1 ? (
                    <Tag color="green" style={{ marginLeft: 12 }}>正常</Tag>
                  ) : (
                    <Tag color="red" style={{ marginLeft: 12 }}>已禁用</Tag>
                  )}
                  {isMuted && <Tag color="orange" style={{ marginLeft: 8 }}>禁言中</Tag>}
                </Title>
                <Text type="secondary">ID: {user.id}</Text>
              </div>
            </div>

            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="昵称">{user.nickname || '-'}</Descriptions.Item>
              <Descriptions.Item label="手机号">{user.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="性别">{genderMap[user.gender] || '-'}</Descriptions.Item>
              <Descriptions.Item label="注册时间">{user.created_at ? new Date(user.created_at).toLocaleString() : '-'}</Descriptions.Item>
              <Descriptions.Item label="最后登录">{user.last_login ? new Date(user.last_login).toLocaleString() : '-'}</Descriptions.Item>
              <Descriptions.Item label="登录次数">{user.login_count || 0}</Descriptions.Item>
              <Descriptions.Item label="禁言到期" span={2}>
                {isMuted ? new Date(user.mute_until).toLocaleString() : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="个性签名" span={2}>
                <Paragraph style={{ marginBottom: 0 }}>{user.signature || '-'}</Paragraph>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* 标签区域 */}
          <Card
            title="用户标签"
            style={{ marginTop: 16 }}
            extra={
              <Button size="small" icon={<PlusOutlined />} onClick={() => setTagModal(true)}>
                添加标签
              </Button>
            }
          >
            <div>
              {tags.length > 0 ? (
                <Space wrap>
                  {tags.map((tag) => (
                    <Tag
                      key={tag.id}
                      color={tag.color || 'blue'}
                      closable
                      onClose={() => handleRemoveTag(tag.id)}
                      closeIcon={<CloseOutlined />}
                    >
                      {tag.name}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <Text type="secondary">暂无标签</Text>
              )}
            </div>
          </Card>
        </Col>

        {/* 右侧操作面板 */}
        <Col span={8}>
          {/* 状态控制 */}
          <Card title="状态控制" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {user.status === 1 ? (
                <Popconfirm title="确定禁用该用户?" onConfirm={handleDisable}>
                  <Button block danger icon={<StopOutlined />}>禁用用户</Button>
                </Popconfirm>
              ) : (
                <Popconfirm title="确定启用该用户?" onConfirm={handleEnable}>
                  <Button block type="primary" icon={<CheckOutlined />}>启用用户</Button>
                </Popconfirm>
              )}
              {isMuted ? (
                <Button block icon={<MutedOutlined />} onClick={handleUnmute}>解除禁言</Button>
              ) : (
                <Button block icon={<SoundOutlined />} onClick={() => setMuteModal(true)}>禁言用户</Button>
              )}
            </Space>
          </Card>

          {/* 活动统计 */}
          <Card title="活动统计">
            <Row gutter={16}>
              <Col span={12}>
                <Statistic title="登录次数" value={user.login_count || 0} />
              </Col>
              <Col span={12}>
                <Statistic title="好友数量" value={user.friend_count || 0} />
              </Col>
            </Row>
            <Divider style={{ margin: '12px 0' }} />
            <Row gutter={16}>
              <Col span={12}>
                <Statistic title="动态数量" value={user.moment_count || 0} />
              </Col>
              <Col span={12}>
                <Statistic title="群组数量" value={user.group_count || 0} />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* 添加标签 Modal */}
      <Modal
        title="添加标签"
        open={tagModal}
        onOk={handleAddTags}
        onCancel={() => { setTagModal(false); setSelectedTags([]); }}
      >
        <Select
          mode="multiple"
          placeholder="选择要添加的标签"
          value={selectedTags}
          onChange={setSelectedTags}
          style={{ width: '100%' }}
          options={availableTags
            .filter(t => !tags.find(ut => ut.id === t.id))
            .map(t => ({ value: t.id, label: t.name }))}
        />
      </Modal>

      {/* 禁言 Modal */}
      <Modal
        title={`禁言用户 - ${user.nickname}`}
        open={muteModal}
        onOk={handleMute}
        onCancel={() => setMuteModal(false)}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>选择禁言时长：</Text>
          <Select value={muteHours} onChange={setMuteHours} style={{ width: '100%' }}>
            <Select.Option value={1}>禁言 1 小时</Select.Option>
            <Select.Option value={6}>禁言 6 小时</Select.Option>
            <Select.Option value={12}>禁言 12 小时</Select.Option>
            <Select.Option value={24}>禁言 24 小时</Select.Option>
            <Select.Option value={72}>禁言 3 天</Select.Option>
            <Select.Option value={168}>禁言 7 天</Select.Option>
            <Select.Option value={720}>禁言 30 天</Select.Option>
          </Select>
        </Space>
      </Modal>
    </>
  );
}

export default UserDetailPage;
